import { useState, useCallback, useMemo, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { useLookupValues } from '@/hooks/useLookupValues';
import { useToast } from '@/components/ui/Toast';
import { usePermissions } from '@/hooks/usePermissions';
import { canAccessReportSource, filterAllowedReportSources } from './reportingAccess';
import {
  DATA_SOURCE_OPTIONS,
  SOURCE_FIELDS,
  FILTER_OPERATORS,
  MAX_REPORT_JOINS,
  MAX_REPORT_FIELDS,
  useReportBuilderPreview,
  useSaveBuilderReport,
  useUpdateBuilderReport,
  useReportingDataRules,
  useBuilderMetadata,
  type DataSource,
  type FieldDefinition,
  type ReportFilter,
  type AggregationConfig,
  type SortConfig,
  type CalculatedField,
  type ReportBuilderQuery,
  type ReportBuilderJoin,
  type ReportBuilderResult,
  type AggregationType,
  type SortDirection,
  type FilterOperator,
  type CalculatedFieldOperation,
  type ReportingDataRule,
  type JoinPath,
  type SavedBuilderReport,
  type CalculatedFieldPayload,
} from './hooks/useReportBuilder';
import { resolveTagSource } from './reportTagSources';
import {
  isStoredApiCalculatedField,
  parseCalculatedFieldExpression,
  syncCalculatedField,
} from './calculatedFieldUtils';

// ─── Helpers ────────────────────────────────────────────────────────────────

let nextFilterId = 1;
function generateFilterId(): string {
  return `filter-${nextFilterId++}`;
}

let nextCalcId = 1;
function generateCalcId(): string {
  return `calc-${nextCalcId++}`;
}

const FINANCIAL_FIELD_RE =
  /(?:^|[._])(amount|total|subtotal|balance|debit|credit|revenue|rate|cost|price|paid|fee|tax|discount|billed|outstanding|receivable|payment)/i;

const LEDGER_AMOUNT_FIELDS = new Set(['amount', 'debit', 'credit', 'balance', 'running_balance']);

function isFinancialBuilderField(field: FieldDefinition): boolean {
  if (
    (field.source === 'client_ledger' || field.source === 'wip_entries') &&
    LEDGER_AMOUNT_FIELDS.has(field.name)
  ) {
    return false;
  }
  const hay = `${field.name} ${field.label}`;
  return FINANCIAL_FIELD_RE.test(hay);
}

type AggregationLike = AggregationConfig & { function?: AggregationType };

function aggregationType(agg: AggregationLike): AggregationType {
  return agg.type ?? agg.function ?? 'sum';
}

function normalizeAggregations(aggs: AggregationLike[] | undefined): AggregationConfig[] {
  return (aggs ?? []).map((agg) => ({
    field: agg.field,
    type: aggregationType(agg),
    ...(agg.alias ? { alias: agg.alias } : {}),
  }));
}

const FILTER_OPERATORS_WITHOUT_VALUE = new Set<FilterOperator>(['is_null', 'is_not_null']);

function filterRequiresValue(operator: FilterOperator): boolean {
  return !FILTER_OPERATORS_WITHOUT_VALUE.has(operator);
}

function isFilterValuePresent(value: string | number | string[] | null | undefined): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (typeof value === 'number') return !Number.isNaN(value);
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

function getMissingFilterValueLabels(filters: ReportFilter[], fields: FieldDefinition[]): string[] {
  return filters
    .filter((f) => filterRequiresValue(f.operator) && !isFilterValuePresent(f.value))
    .map((f) => fields.find((field) => field.id === f.field)?.label ?? f.field);
}

function toCalculatedFieldPayload(cf: CalculatedField): CalculatedFieldPayload {
  const synced = syncCalculatedField(cf);
  if (isStoredApiCalculatedField(synced)) {
    return { alias: synced.alias, expression: synced.expression };
  }
  const name =
    synced.name.trim() || synced.label.trim().toLowerCase().replace(/\s+/g, '_') || 'calculated';
  return {
    name,
    label: synced.label.trim() || name.replace(/_/g, ' '),
    operation: synced.operation,
    fieldA: synced.fieldA,
    fieldB: synced.fieldB,
  };
}

function loadCalculatedFieldFromQuery(cf: CalculatedFieldPayload, index: number): CalculatedField {
  if (isStoredApiCalculatedField(cf)) {
    const parsed = parseCalculatedFieldExpression(cf.expression);
    return syncCalculatedField({
      id: `calc-loaded-${index}`,
      name: cf.alias,
      label: cf.alias.replace(/_/g, ' '),
      operation: parsed?.operation ?? 'add',
      fieldA: parsed?.fieldA ?? '',
      fieldB: parsed?.fieldB ?? '',
      alias: cf.alias,
      expression: cf.expression,
    });
  }
  const legacy = cf as Pick<CalculatedField, 'name' | 'label' | 'operation' | 'fieldA' | 'fieldB'>;
  return syncCalculatedField({
    id: `calc-loaded-${index}`,
    name: legacy.name,
    label: legacy.label ?? legacy.name,
    operation: legacy.operation,
    fieldA: legacy.fieldA,
    fieldB: legacy.fieldB,
  });
}

function getInvalidCalculatedFieldLabels(calculatedFields: CalculatedField[]): string[] {
  return calculatedFields
    .filter((cf) => !isStoredApiCalculatedField(cf))
    .filter((cf) => !cf.name.trim() || !cf.fieldA.trim() || !cf.fieldB.trim())
    .map((cf) => cf.label.trim() || cf.name.trim() || 'Calculated field');
}

// ─── Collapsible Section ────────────────────────────────────────────────────

interface SectionProps {
  title: string;
  subtitle?: string;
  step?: number;
  badge?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

function Section({ title, subtitle, step, badge, defaultOpen = true, children }: SectionProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="rounded-xl border border-border bg-white shadow-soft dark:border-border-dark dark:bg-surface-dark-secondary">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-3 px-5 py-4 text-left"
        aria-expanded={open}
      >
        {step && (
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-bold text-brand-700 dark:bg-brand-900/30 dark:text-brand-300">
            {step}
          </span>
        )}
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</span>
            {badge && (
              <Badge variant="info" size="sm">
                {badge}
              </Badge>
            )}
          </div>
          {subtitle && (
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>
          )}
        </div>
        <svg
          className={`h-5 w-5 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="border-t border-border px-5 pb-5 pt-4 dark:border-border-dark">
          {children}
        </div>
      )}
    </div>
  );
}

type ReportLayoutMode = 'detail' | 'summary';

function ReportLayoutModeToggle({
  mode,
  onModeChange,
}: {
  mode: ReportLayoutMode;
  onModeChange: (mode: ReportLayoutMode) => void;
}) {
  return (
    <div className="rounded-xl border border-border bg-surface-secondary/50 p-4 dark:border-border-dark dark:bg-surface-dark-tertiary/50">
      <p className="mb-3 text-sm font-medium text-gray-900 dark:text-gray-100">Report layout</p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => onModeChange('detail')}
          className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
            mode === 'detail'
              ? 'bg-brand-600 text-white shadow-sm'
              : 'bg-white text-gray-700 ring-1 ring-border hover:bg-gray-50 dark:bg-surface-dark-secondary dark:text-gray-200 dark:ring-border-dark'
          }`}
        >
          Detail
        </button>
        <button
          type="button"
          onClick={() => onModeChange('summary')}
          className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
            mode === 'summary'
              ? 'bg-brand-600 text-white shadow-sm'
              : 'bg-white text-gray-700 ring-1 ring-border hover:bg-gray-50 dark:bg-surface-dark-secondary dark:text-gray-200 dark:ring-border-dark'
          }`}
        >
          Summary
        </button>
      </div>
      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
        {mode === 'detail'
          ? 'Pick columns and optional calculated fields — one row per record.'
          : 'Pick group-by dimensions and aggregations — one row per group.'}
      </p>
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

export interface ReportBuilderPermissions {
  canRun: boolean;
  canGenerate: boolean;
  canExport: boolean;
  canFinancials: boolean;
}

export interface ReportBuilderProps {
  initialQuery?: ReportBuilderQuery;
  editingReportId?: string;
  editingReportName?: string;
  onBack?: () => void;
  onRunComplete?: (result: ReportBuilderResult, reportName?: string) => void;
  onSaveComplete?: (saved: SavedBuilderReport) => void;
  permissions?: ReportBuilderPermissions;
}

/**
 * ReportBuilder — Dynamic report builder with step-by-step UX.
 * Requirements: 24.2, 24.3, 24.19
 */
export function ReportBuilder({
  initialQuery,
  editingReportId,
  editingReportName,
  onRunComplete,
  onSaveComplete,
  permissions,
}: ReportBuilderProps) {
  const canRun = permissions?.canRun !== false;
  const canGenerate = permissions?.canGenerate !== false;
  const canFinancials = permissions?.canFinancials !== false;
  const [source, setSource] = useState<DataSource | ''>('');
  const [joins, setJoins] = useState<ReportBuilderJoin[]>([]);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [includeTags, setIncludeTags] = useState(false);
  const [filters, setFilters] = useState<ReportFilter[]>([]);
  const [groupBy, setGroupBy] = useState<string[]>([]);
  const [aggregations, setAggregations] = useState<AggregationConfig[]>([]);
  const [sortConfig, setSortConfig] = useState<SortConfig[]>([]);
  const [calculatedFields, setCalculatedFields] = useState<CalculatedField[]>([]);
  const [limit, setLimit] = useState<number>(100);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [selectedDataRuleIds, setSelectedDataRuleIds] = useState<string[]>([]);
  const [layoutMode, setLayoutMode] = useState<ReportLayoutMode>('detail');

  const previewMutation = useReportBuilderPreview();
  const saveMutation = useSaveBuilderReport();
  const updateMutation = useUpdateBuilderReport();
  const { toast } = useToast();
  const { data: dataRules } = useReportingDataRules();
  const { data: metadata } = useBuilderMetadata();

  // Report groups (data sources) are gated by the caller's module read
  // permissions — a source is only offered when the user can read that module.
  const { can, canAny, isAdmin } = usePermissions();
  const permChecker = useMemo(() => ({ isAdmin, can, canAny }), [isAdmin, can, canAny]);
  const allowedSourceOptions = useMemo(
    () => filterAllowedReportSources(permChecker, DATA_SOURCE_OPTIONS),
    [permChecker],
  );

  useEffect(() => {
    if (!initialQuery) return;
    const summary =
      (initialQuery.group_by?.length ?? 0) > 0 || (initialQuery.aggregations?.length ?? 0) > 0;
    setSource(initialQuery.source as DataSource);
    setJoins((initialQuery.joins ?? []) as ReportBuilderJoin[]);
    setSelectedFields(initialQuery.fields);
    setIncludeTags(summary ? false : (initialQuery.include_tags ?? false));
    setFilters(
      (initialQuery.filters ?? []).map((f, i) => ({
        id: `loaded-${i}`,
        ...f,
      })) as ReportFilter[],
    );
    setGroupBy(initialQuery.group_by ?? []);
    setAggregations(
      normalizeAggregations(initialQuery.aggregations as AggregationLike[] | undefined),
    );
    setSortConfig((initialQuery.sort ?? []) as SortConfig[]);
    setCalculatedFields(
      (initialQuery.calculated_fields ?? []).map((cf, i) => loadCalculatedFieldFromQuery(cf, i)),
    );
    setLimit(initialQuery.limit ?? 100);
    setLayoutMode(summary ? 'summary' : 'detail');
  }, [initialQuery]);

  const tagSource = useMemo(
    () => (source ? resolveTagSource(source as DataSource, joins) : null),
    [source, joins],
  );

  const tagSourceLabel = useMemo(
    () => DATA_SOURCE_OPTIONS.find((o) => o.value === tagSource)?.label ?? tagSource,
    [tagSource],
  );

  useEffect(() => {
    if (!tagSource && includeTags) {
      setIncludeTags(false);
    }
  }, [tagSource, includeTags]);

  const handleLayoutModeChange = useCallback((mode: ReportLayoutMode) => {
    setLayoutMode(mode);
    if (mode === 'summary') {
      setSelectedFields([]);
      setCalculatedFields([]);
      setIncludeTags(false);
    } else {
      setGroupBy([]);
      setAggregations([]);
    }
  }, []);

  const availableFields = useMemo(() => {
    if (!source) return [];
    const primary = SOURCE_FIELDS[source] ?? [];

    // Add fields from joined sources with table prefix
    const joinedFields: FieldDefinition[] = [];
    for (const join of joins) {
      const joinSource = join.source as DataSource;
      const sourceFields = SOURCE_FIELDS[joinSource] ?? [];
      for (const f of sourceFields) {
        joinedFields.push({
          ...f,
          id: `${joinSource}.${f.name}`,
          label: `${join.label || joinSource} → ${f.label}`,
          category: join.label || joinSource,
        });
      }
    }

    // Add form builder fields for this entity scope
    const formBuilderFields: FieldDefinition[] = [];
    if (metadata?.formFields) {
      const entityMap: Record<string, string> = {
        contacts: 'contact',
        jobs: 'job',
        billing: 'invoice',
        time_entries: 'time_entry',
      };
      const entityScope = entityMap[source];
      const matchingFields = metadata.formFields.filter((f) => f.entityScope === entityScope);
      // Deduplicate: skip form fields whose dbColumn is already in the primary fields
      const primaryIds = new Set(primary.map((f) => f.name || f.id));
      for (const f of matchingFields) {
        const knownColumn = Boolean(f.dbColumn && primaryIds.has(f.dbColumn));
        const fieldId = knownColumn ? `${source}.${f.dbColumn}` : `${source}.${f.fieldKey}`;
        // Skip if already in primary fields
        if (primaryIds.has(f.dbColumn || '') || primaryIds.has(f.fieldKey)) continue;
        formBuilderFields.push({
          id: fieldId,
          name: f.fieldKey,
          label: f.label,
          type: f.fieldType === 'number' ? 'number' : f.fieldType === 'date' ? 'date' : 'text',
          source: source,
          category: `${f.formName} → ${f.section}`,
          lookupCategory: f.lookupCategory,
          staticOptions: f.options && f.options.length > 0 ? f.options : undefined,
        });
      }
    }

    // Add tags when enabled and a tag-capable source is available (main or join)
    const tagsField: FieldDefinition[] =
      includeTags && layoutMode !== 'summary' && tagSource
        ? [
            {
              id: 'tags',
              name: 'tags',
              label: tagSource === source ? 'Tags' : `Tags (${tagSourceLabel})`,
              type: 'text',
              source: tagSource,
              category: 'Tags',
            },
          ]
        : [];

    const all = [...primary, ...joinedFields, ...formBuilderFields, ...tagsField];
    if (canFinancials) return all;
    return all.filter((f) => !isFinancialBuilderField(f));
  }, [source, joins, metadata, includeTags, layoutMode, canFinancials, tagSource, tagSourceLabel]);

  const defaultAggAlias = (field: string, type: AggregationType): string => {
    const base = field.replace(/\./g, '_').replace(/[^a-zA-Z0-9_]/g, '_') || 'field';
    return `${type}_${base}`.slice(0, 100);
  };

  const sortFieldOptions = useMemo((): FieldDefinition[] => {
    if (layoutMode !== 'summary') return availableFields;
    const opts: FieldDefinition[] = [];
    const seen = new Set<string>();
    const add = (f: FieldDefinition) => {
      if (seen.has(f.id)) return;
      seen.add(f.id);
      opts.push(f);
    };
    for (const id of groupBy) {
      const found = availableFields.find((f) => f.id === id);
      if (found) add(found);
      else {
        add({
          id,
          name: id,
          label: id.includes('.') ? id.split('.').slice(1).join('.') : id,
          type: 'text',
          source: source as DataSource,
          category: 'Group by',
        });
      }
    }
    for (const agg of aggregations) {
      const fn = aggregationType(agg);
      const alias = agg.alias?.trim() || defaultAggAlias(agg.field, fn);
      const base = availableFields.find((f) => f.id === agg.field);
      const fnLabel = fn.charAt(0).toUpperCase() + fn.slice(1);
      add({
        id: alias,
        name: alias,
        label: base ? `${fnLabel} of ${base.label}` : `${fnLabel} (${agg.field})`,
        type: 'number',
        source: (source || 'contacts') as DataSource,
        category: 'Aggregation',
      });
    }
    return opts.length > 0 ? opts : availableFields;
  }, [layoutMode, availableFields, groupBy, aggregations, source]);

  const numericFields = useMemo(
    () => availableFields.filter((f) => f.type === 'number'),
    [availableFields],
  );

  const handleSourceChange = useCallback((newSource: DataSource) => {
    setSource(newSource);
    setJoins([]);
    setSelectedFields([]);
    setIncludeTags(false);
    setFilters([]);
    setGroupBy([]);
    setAggregations([]);
    setSortConfig([]);
    setCalculatedFields([]);
    setSelectedDataRuleIds([]);
  }, []);

  const fieldBelongsToJoin = useCallback((fieldId: string, joinSource: string) => {
    return fieldId === joinSource || fieldId.startsWith(`${joinSource}.`);
  }, []);

  const handleJoinsChange = useCallback(
    (nextJoins: ReportBuilderJoin[]) => {
      const removedSources = joins
        .filter((join) => !nextJoins.some((next) => next.source === join.source))
        .map((join) => join.source);
      setJoins(nextJoins);
      if (removedSources.length === 0) return;

      const belongsToRemoved = (fieldId: string) =>
        removedSources.some((joinSource) => fieldBelongsToJoin(fieldId, joinSource));

      setSelectedFields((prev) => prev.filter((id) => !belongsToRemoved(id)));
      setFilters((prev) => prev.filter((filter) => !belongsToRemoved(filter.field)));
      setGroupBy((prev) => prev.filter((field) => !belongsToRemoved(field)));
      setAggregations((prev) => prev.filter((agg) => !belongsToRemoved(agg.field)));
      setSortConfig((prev) => prev.filter((entry) => !belongsToRemoved(entry.field)));
      setCalculatedFields((prev) =>
        prev.filter((calc) => !belongsToRemoved(calc.fieldA) && !belongsToRemoved(calc.fieldB)),
      );
    },
    [joins, fieldBelongsToJoin],
  );

  const handleToggleField = useCallback((fieldId: string) => {
    setSelectedFields((prev) => {
      if (prev.includes(fieldId)) return prev.filter((f) => f !== fieldId);
      if (prev.length >= MAX_REPORT_FIELDS) return prev;
      return [...prev, fieldId];
    });
  }, []);

  const handleAddFilter = useCallback(() => {
    if (!availableFields.length) return;
    setFilters((prev) => [
      ...prev,
      { id: generateFilterId(), field: availableFields[0].id, operator: 'equals', value: '' },
    ]);
  }, [availableFields]);

  const handleUpdateFilter = useCallback((id: string, updates: Partial<ReportFilter>) => {
    setFilters((prev) => prev.map((f) => (f.id === id ? { ...f, ...updates } : f)));
  }, []);

  const handleRemoveFilter = useCallback((id: string) => {
    setFilters((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const handleAddCalculatedField = useCallback(() => {
    if (numericFields.length < 2) return;
    setCalculatedFields((prev) => [
      ...prev,
      syncCalculatedField({
        id: generateCalcId(),
        name: '',
        label: '',
        operation: 'add' as CalculatedFieldOperation,
        fieldA: numericFields[0]?.id ?? '',
        fieldB: numericFields[1]?.id ?? numericFields[0]?.id ?? '',
      }),
    ]);
  }, [numericFields]);

  const handleUpdateCalculatedField = useCallback(
    (id: string, updates: Partial<CalculatedField>) => {
      setCalculatedFields((prev) =>
        prev.map((f) => (f.id === id ? syncCalculatedField({ ...f, ...updates }) : f)),
      );
    },
    [],
  );

  const handleRemoveCalculatedField = useCallback((id: string) => {
    setCalculatedFields((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const isSummaryReport = layoutMode === 'summary';

  const hasValidReportConfig =
    !!source &&
    (isSummaryReport ? groupBy.length > 0 || aggregations.length > 0 : selectedFields.length > 0);

  const notifyMissingFilterValues = useCallback((): boolean => {
    const missing = getMissingFilterValueLabels(filters, availableFields);
    if (missing.length === 0) return true;
    toast({
      variant: 'error',
      title: 'Required fields missing',
      description: `Please fill in: ${missing.join(', ')}`,
    });
    return false;
  }, [filters, availableFields, toast]);

  const notifyInvalidCalculatedFields = useCallback((): boolean => {
    const invalid = getInvalidCalculatedFieldLabels(calculatedFields);
    if (invalid.length === 0) return true;
    toast({
      variant: 'error',
      title: 'Calculated fields incomplete',
      description: `Please complete: ${invalid.join(', ')}`,
    });
    return false;
  }, [calculatedFields, toast]);

  useEffect(() => {
    if (!isSummaryReport) return;
    setSelectedFields([]);
    setCalculatedFields([]);
  }, [isSummaryReport]);

  const summaryQueryFields = useCallback((): string[] => {
    if (!isSummaryReport) return selectedFields;
    const dims = [...new Set(groupBy)];
    if (dims.length > 0) return dims;
    if (aggregations.length > 0) return [aggregations[0].field];
    return ['id'];
  }, [isSummaryReport, groupBy, aggregations, selectedFields]);

  const buildQuery = useCallback((): ReportBuilderQuery => {
    const query: ReportBuilderQuery = {
      source: source as DataSource,
      joins: joins.length > 0 ? joins : undefined,
      fields: summaryQueryFields(),
      filters: filters.map(({ field, operator, value }) => ({ field, operator, value })),
      sort: sortConfig.length > 0 ? sortConfig : undefined,
      include_tags: !isSummaryReport && includeTags && tagSource ? true : undefined,
      limit,
      data_rule_ids: selectedDataRuleIds.length > 0 ? selectedDataRuleIds : undefined,
    };
    if (groupBy.length > 0) {
      query.group_by = groupBy;
    }
    if (aggregations.length > 0) {
      query.aggregations = aggregations;
    }
    if (!isSummaryReport && calculatedFields.length > 0) {
      query.calculated_fields = calculatedFields.map(toCalculatedFieldPayload);
    }
    return query;
  }, [
    source,
    joins,
    filters,
    groupBy,
    aggregations,
    summaryQueryFields,
    sortConfig,
    calculatedFields,
    limit,
    selectedDataRuleIds,
    includeTags,
    isSummaryReport,
    tagSource,
  ]);

  const handlePreview = useCallback(() => {
    if (!canRun || !source || !hasValidReportConfig) return;
    if (!notifyMissingFilterValues()) return;
    if (!notifyInvalidCalculatedFields()) return;
    const query = buildQuery();
    previewMutation.mutate(query, {
      onSuccess: (data) => onRunComplete?.(data, editingReportName),
    });
  }, [
    canRun,
    source,
    hasValidReportConfig,
    notifyMissingFilterValues,
    notifyInvalidCalculatedFields,
    buildQuery,
    previewMutation,
    onRunComplete,
    editingReportName,
  ]);

  const handleSave = useCallback(
    (name: string, description?: string) => {
      if (!hasValidReportConfig) return;
      if (!notifyMissingFilterValues()) return;
      if (!notifyInvalidCalculatedFields()) return;
      const query = buildQuery();
      saveMutation.mutate(
        { name, description, query },
        {
          onSuccess: (saved) => {
            setShowSaveDialog(false);
            toast({
              variant: 'success',
              title: 'Report saved',
              description: `"${name}" was saved successfully.`,
            });
            onSaveComplete?.(saved);
          },
        },
      );
    },
    [
      hasValidReportConfig,
      notifyMissingFilterValues,
      notifyInvalidCalculatedFields,
      buildQuery,
      saveMutation,
      toast,
      onSaveComplete,
    ],
  );

  const handleUpdate = useCallback(() => {
    if (!editingReportId || !hasValidReportConfig) return;
    if (!notifyMissingFilterValues()) return;
    if (!notifyInvalidCalculatedFields()) return;
    const name = editingReportName || 'Untitled Report';
    const query = buildQuery();
    updateMutation.mutate(
      {
        id: editingReportId,
        payload: { name, query },
      },
      {
        onSuccess: () => {
          toast({
            variant: 'success',
            title: 'Report updated',
            description: `"${name}" was updated successfully.`,
          });
        },
      },
    );
  }, [
    editingReportId,
    editingReportName,
    hasValidReportConfig,
    notifyMissingFilterValues,
    notifyInvalidCalculatedFields,
    buildQuery,
    updateMutation,
    toast,
  ]);

  const canRunReport = canRun && hasValidReportConfig;
  const canSaveReport = canGenerate && hasValidReportConfig;

  return (
    <div className="space-y-4">
      {/* Step 1: Data Source */}
      <Section step={1} title="Choose Data Source" subtitle="Select which module to query">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {allowedSourceOptions.map((ds) => (
            <button
              key={ds.value}
              type="button"
              onClick={() => handleSourceChange(ds.value)}
              className={`rounded-lg border-2 p-3 text-left transition-all ${
                source === ds.value
                  ? 'border-brand-500 bg-brand-50 dark:border-brand-400 dark:bg-brand-900/20'
                  : 'border-border hover:border-gray-300 dark:border-border-dark dark:hover:border-gray-600'
              }`}
            >
              <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                {ds.label}
              </span>
              <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                {ds.description}
              </span>
            </button>
          ))}
        </div>
      </Section>

      {source && (
        <>
          <ReportLayoutModeToggle mode={layoutMode} onModeChange={handleLayoutModeChange} />

          {/* Step 2: Join Sources */}
          <Section
            step={2}
            title="Join Sources"
            subtitle="Connect related data tables"
            badge={`${joins.length}/${MAX_REPORT_JOINS} joined`}
            defaultOpen={joins.length > 0}
          >
            <JoinSourcesSection
              source={source as DataSource}
              joins={joins}
              metadata={metadata}
              maxJoins={MAX_REPORT_JOINS}
              onJoinsChange={handleJoinsChange}
            />
          </Section>

          {!isSummaryReport && (
            <Section
              step={3}
              title="Select Fields"
              subtitle="Choose which columns to include"
              badge={`${selectedFields.length}/${MAX_REPORT_FIELDS} selected`}
            >
              <FieldPicker
                fields={availableFields}
                selectedFields={selectedFields}
                maxFields={MAX_REPORT_FIELDS}
                onToggle={handleToggleField}
              />
              <div className="mt-4 flex items-center gap-6 border-t border-border pt-4 dark:border-border-dark">
                {tagSource && metadata?.tags && metadata.tags.length > 0 && (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeTags}
                      onChange={(e) => setIncludeTags(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Include Tags
                      {tagSource !== source && tagSourceLabel ? ` (${tagSourceLabel})` : ''}
                    </span>
                    <span className="text-xs text-gray-400">
                      ({metadata.tags.length} available)
                    </span>
                  </label>
                )}
              </div>
            </Section>
          )}

          {isSummaryReport && (
            <Section
              step={3}
              title="Report columns"
              subtitle="Group-by dimensions and aggregation metrics"
              badge={`${groupBy.length} group · ${aggregations.length} agg`}
              defaultOpen
            >
              <GroupBySection
                groupBy={groupBy}
                aggregations={aggregations}
                fields={availableFields}
                numericFields={numericFields}
                onGroupByChange={setGroupBy}
                onAggregationsChange={setAggregations}
              />
            </Section>
          )}

          {/* Step 4: Filters (optional) */}
          <Section
            step={4}
            title="Filters"
            subtitle="Narrow down your results"
            badge={filters.length > 0 ? `${filters.length} active` : undefined}
            defaultOpen={filters.length > 0}
          >
            <FilterBuilder
              filters={filters}
              fields={availableFields}
              onAdd={handleAddFilter}
              onUpdate={handleUpdateFilter}
              onRemove={handleRemoveFilter}
            />
          </Section>

          {/* Data Rule Presets (optional) */}
          {dataRules && dataRules.length > 0 && (
            <Section
              step={5}
              title="Data Filters"
              subtitle="Apply saved data authorization rules as report filters"
              badge={
                selectedDataRuleIds.length > 0 ? `${selectedDataRuleIds.length} applied` : undefined
              }
              defaultOpen={selectedDataRuleIds.length > 0}
            >
              <DataRulePresets
                rules={dataRules}
                selectedIds={selectedDataRuleIds}
                source={source as DataSource}
                onSelectionChange={setSelectedDataRuleIds}
              />
            </Section>
          )}

          {/* Sort & Limit */}
          <Section
            step={7}
            title="Sort & Limit"
            subtitle={
              isSummaryReport
                ? 'Sort grouped rows (dimensions or metrics); max rows caps groups returned'
                : 'Order results and set row cap'
            }
            defaultOpen={sortConfig.length > 0}
          >
            <div className="space-y-4">
              <SortSection
                sortConfig={sortConfig}
                fields={sortFieldOptions}
                onSortChange={setSortConfig}
              />
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Max rows:
                </label>
                <input
                  type="number"
                  min={1}
                  max={10000}
                  value={limit}
                  onChange={(e) => setLimit(Number(e.target.value) || 100)}
                  className="w-28 rounded-lg border border-border px-3 py-1.5 text-sm dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
                />
              </div>
            </div>
          </Section>

          {/* Calculated Fields (optional, advanced) */}
          {!isSummaryReport && numericFields.length >= 2 && (
            <Section
              step={8}
              title="Calculated Fields"
              subtitle="Derive new metrics from numeric data"
              defaultOpen={calculatedFields.length > 0}
            >
              <CalculatedFieldsSection
                calculatedFields={calculatedFields}
                numericFields={numericFields}
                onAdd={handleAddCalculatedField}
                onUpdate={handleUpdateCalculatedField}
                onRemove={handleRemoveCalculatedField}
              />
            </Section>
          )}

          {/* Action Bar */}
          <div className="sticky bottom-4 z-10 flex items-center justify-between rounded-xl border border-border bg-white/95 px-5 py-3 shadow-lg backdrop-blur-sm dark:border-border-dark dark:bg-surface-dark-secondary/95">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {isSummaryReport ? (
                <>
                  Summary: {groupBy.length} group field{groupBy.length !== 1 ? 's' : ''},{' '}
                  {aggregations.length} aggregation{aggregations.length !== 1 ? 's' : ''}
                </>
              ) : (
                <>
                  {selectedFields.length} fields from <span className="font-medium">{source}</span>
                </>
              )}
              {joins.length > 0 && ` + ${joins.length} joined`}
              {filters.length > 0 && ` · ${filters.length} filters`}
              {selectedDataRuleIds.length > 0 && ` · ${selectedDataRuleIds.length} data rules`}
              {groupBy.length > 0 && ` · grouped`}
              {includeTags && !isSummaryReport && ` · tags`}
            </p>
            <div className="flex items-center gap-2">
              {editingReportId && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleUpdate}
                  disabled={!canSaveReport || updateMutation.isPending}
                  loading={updateMutation.isPending}
                >
                  {updateMutation.isPending ? 'Updating...' : 'Update'}
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSaveDialog(true)}
                disabled={!canSaveReport}
              >
                {editingReportId ? 'Save As New' : 'Save'}
              </Button>
              <Button
                size="sm"
                onClick={handlePreview}
                disabled={!canRunReport || previewMutation.isPending}
                loading={previewMutation.isPending}
              >
                {previewMutation.isPending ? 'Running...' : 'Run Report'}
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Save Dialog */}
      {showSaveDialog && (
        <SaveReportDialog
          onSave={handleSave}
          onCancel={() => setShowSaveDialog(false)}
          isSaving={saveMutation.isPending}
        />
      )}
    </div>
  );
}

// ─── Field Picker ───────────────────────────────────────────────────────────

interface FieldPickerProps {
  fields: FieldDefinition[];
  selectedFields: string[];
  maxFields: number;
  onToggle: (fieldId: string) => void;
}

function FieldPicker({ fields, selectedFields, maxFields, onToggle }: FieldPickerProps) {
  const [search, setSearch] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const unselectedFields = useMemo(
    () =>
      fields.filter(
        (f) =>
          !selectedFields.includes(f.id) &&
          (search === '' ||
            f.label.toLowerCase().includes(search.toLowerCase()) ||
            f.category.toLowerCase().includes(search.toLowerCase())),
      ),
    [fields, selectedFields, search],
  );

  const selectedFieldDefs = useMemo(
    () =>
      selectedFields
        .map((id) => fields.find((f) => f.id === id))
        .filter(Boolean) as FieldDefinition[],
    [fields, selectedFields],
  );

  const atFieldLimit = selectedFields.length >= maxFields;

  const handleAdd = (fieldId: string) => {
    if (atFieldLimit) return;
    onToggle(fieldId);
    setSearch('');
  };

  return (
    <div className="space-y-3">
      {/* Dropdown to add fields */}
      <div className="relative">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setDropdownOpen(true);
              }}
              onFocus={() => setDropdownOpen(true)}
              placeholder="Search and add fields..."
              className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm placeholder-gray-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100 dark:placeholder-gray-500"
            />
            {dropdownOpen && !atFieldLimit && unselectedFields.length > 0 && (
              <div className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-border bg-white shadow-lg dark:border-border-dark dark:bg-surface-dark-secondary">
                {unselectedFields.slice(0, 50).map((field) => (
                  <button
                    key={field.id}
                    type="button"
                    onClick={() => handleAdd(field.id)}
                    className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-surface-secondary dark:hover:bg-surface-dark-tertiary"
                  >
                    <span className="text-gray-900 dark:text-gray-100">{field.label}</span>
                    <span className="text-[10px] text-gray-400">{field.category}</span>
                  </button>
                ))}
                {unselectedFields.length > 50 && (
                  <p className="px-3 py-2 text-xs text-gray-400">
                    Type to filter {unselectedFields.length - 50} more...
                  </p>
                )}
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            disabled={atFieldLimit}
            onClick={() => {
              const remaining = maxFields - selectedFields.length;
              fields
                .filter((f) => !selectedFields.includes(f.id))
                .slice(0, remaining)
                .forEach((f) => onToggle(f.id));
            }}
          >
            All
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => selectedFields.forEach((id) => onToggle(id))}
          >
            Clear
          </Button>
        </div>
        {/* Click outside to close */}
        {dropdownOpen && (
          <div className="fixed inset-0 z-10" onClick={() => setDropdownOpen(false)} />
        )}
      </div>

      {/* Selected fields as removable chips */}
      {selectedFieldDefs.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedFieldDefs.map((field) => (
            <span
              key={field.id}
              className="inline-flex items-center gap-1 rounded-md bg-brand-50 px-2 py-1 text-xs font-medium text-brand-700 ring-1 ring-brand-200 dark:bg-brand-900/20 dark:text-brand-300 dark:ring-brand-800"
            >
              {field.label}
              <button
                type="button"
                onClick={() => onToggle(field.id)}
                className="ml-0.5 text-brand-400 hover:text-brand-600 dark:text-brand-500 dark:hover:text-brand-300"
                aria-label={`Remove ${field.label}`}
              >
                <svg
                  className="h-3 w-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}

      {selectedFields.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No fields selected. Search above to add columns to your report.
        </p>
      )}
      {atFieldLimit && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Maximum {maxFields} fields allowed.
        </p>
      )}
    </div>
  );
}

// ─── Filter Builder ─────────────────────────────────────────────────────────

interface FilterBuilderProps {
  filters: ReportFilter[];
  fields: FieldDefinition[];
  onAdd: () => void;
  onUpdate: (id: string, updates: Partial<ReportFilter>) => void;
  onRemove: (id: string) => void;
}

function FilterBuilder({ filters, fields, onAdd, onUpdate, onRemove }: FilterBuilderProps) {
  const getFieldType = useCallback(
    (fieldId: string) => fields.find((f) => f.id === fieldId)?.type ?? 'text',
    [fields],
  );

  const getFieldDef = useCallback(
    (fieldId: string) => fields.find((f) => f.id === fieldId),
    [fields],
  );

  const getOperatorsForField = useCallback(
    (fieldId: string) => {
      const fieldType = getFieldType(fieldId);
      return FILTER_OPERATORS.filter((op) => op.types.includes(fieldType));
    },
    [getFieldType],
  );

  return (
    <div className="space-y-3">
      {filters.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No filters yet. Add conditions to narrow your results.
        </p>
      )}
      {filters.map((filter) => {
        const valueRequired = filterRequiresValue(filter.operator);
        return (
          <div
            key={filter.id}
            className="grid grid-cols-1 items-start gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto]"
          >
            <Select
              label="Field"
              value={filter.field}
              onChange={(e) => onUpdate(filter.id, { field: e.target.value, value: '' })}
              options={fields.map((f) => ({ value: f.id, label: f.label }))}
            />
            <Select
              label="Operator"
              value={filter.operator}
              onChange={(e) => onUpdate(filter.id, { operator: e.target.value as FilterOperator })}
              options={getOperatorsForField(filter.field).map((op) => ({
                value: op.value,
                label: op.label,
              }))}
            />
            <div className="min-w-0">
              {valueRequired && (
                <FilterValueInput
                  fieldDef={getFieldDef(filter.field)}
                  value={filter.value}
                  required
                  onChange={(val) => onUpdate(filter.id, { value: val })}
                />
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="sm:mt-7"
              onClick={() => onRemove(filter.id)}
              aria-label="Remove"
            >
              <svg
                className="h-4 w-4 text-gray-400 hover:text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </Button>
          </div>
        );
      })}
      <Button variant="outline" size="sm" onClick={onAdd}>
        + Add Filter
      </Button>
    </div>
  );
}

// ─── Filter Value Input ─────────────────────────────────────────────────────

interface FilterValueInputProps {
  fieldDef: FieldDefinition | undefined;
  value: string | number | string[] | null;
  required?: boolean;
  onChange: (value: string | number | string[] | null) => void;
}

function FilterValueInput({ fieldDef, value, required = false, onChange }: FilterValueInputProps) {
  const { options, isLoading } = useLookupValues(fieldDef?.lookupCategory, {
    enabled: !!(fieldDef?.lookupCategory || fieldDef?.staticOptions),
    staticOptions: fieldDef?.staticOptions,
    fieldKey: fieldDef?.name,
  });

  const hasLookup = !!(
    fieldDef?.lookupCategory ||
    (fieldDef?.staticOptions && fieldDef.staticOptions.length > 0)
  );

  // Lookup field — show dropdown
  if (hasLookup || options.length > 0) {
    if (isLoading) {
      return <Input label="Value" value="" placeholder="Loading..." disabled required={required} />;
    }
    return (
      <Select
        label="Value"
        value={String(value ?? '')}
        onChange={(e) => onChange(e.target.value)}
        options={options.map((o) => ({ value: o.value, label: o.label }))}
        placeholder="Select..."
        required={required}
      />
    );
  }

  // Tags field — also show as dropdown from tags lookup
  if (fieldDef?.id === 'tags' || fieldDef?.name === 'tags') {
    return <TagsFilterInput value={value} required={required} onChange={onChange} />;
  }

  // Regular field — text/number/date input
  return (
    <Input
      label="Value"
      type={fieldDef?.type === 'number' ? 'number' : fieldDef?.type === 'date' ? 'date' : 'text'}
      value={String(value ?? '')}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Value..."
      required={required}
    />
  );
}

// ─── Tags Filter Input ──────────────────────────────────────────────────────

function TagsFilterInput({
  value,
  required = false,
  onChange,
}: {
  value: string | number | string[] | null;
  required?: boolean;
  onChange: (v: string) => void;
}) {
  const { options, isLoading } = useLookupValues('tags', { enabled: true });

  if (isLoading) {
    return (
      <Input label="Value" value="" placeholder="Loading tags..." disabled required={required} />
    );
  }

  return (
    <Select
      label="Value"
      value={String(value ?? '')}
      onChange={(e) => onChange(e.target.value)}
      options={options.map((o) => ({ value: o.value, label: o.label }))}
      placeholder="Select tag..."
      required={required}
    />
  );
}

// ─── Group By Section ───────────────────────────────────────────────────────

interface GroupBySectionProps {
  groupBy: string[];
  aggregations: AggregationConfig[];
  fields: FieldDefinition[];
  numericFields: FieldDefinition[];
  onGroupByChange: (groupBy: string[]) => void;
  onAggregationsChange: (agg: AggregationConfig[]) => void;
}

function GroupBySection({
  groupBy,
  aggregations,
  fields,
  numericFields,
  onGroupByChange,
  onAggregationsChange,
}: GroupBySectionProps) {
  return (
    <div className="space-y-4">
      {/* Group By */}
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Group By
        </p>
        {groupBy.length === 0 && (
          <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">No grouping applied.</p>
        )}
        <div className="space-y-2">
          {groupBy.map((fieldId, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <Select
                value={fieldId}
                onChange={(e) => {
                  const updated = [...groupBy];
                  updated[idx] = e.target.value;
                  onGroupByChange(updated);
                }}
                options={fields.map((f) => ({ value: f.id, label: f.label }))}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onGroupByChange(groupBy.filter((_, i) => i !== idx))}
                aria-label="Remove"
              >
                <svg
                  className="h-4 w-4 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </Button>
            </div>
          ))}
        </div>
        <Button
          variant="outline"
          size="sm"
          className="mt-2"
          onClick={() => {
            const first = fields.find((f) => !groupBy.includes(f.id));
            if (first) onGroupByChange([...groupBy, first.id]);
          }}
        >
          + Add Group By
        </Button>
      </div>

      {/* Aggregations */}
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Aggregations
        </p>
        {aggregations.length === 0 && (
          <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">No aggregations.</p>
        )}
        <div className="space-y-2">
          {aggregations.map((agg, idx) => (
            <div key={idx} className="flex items-end gap-2">
              <Select
                label="Function"
                value={aggregationType(agg)}
                onChange={(e) => {
                  const updated = [...aggregations];
                  updated[idx] = { ...agg, type: e.target.value as AggregationType };
                  onAggregationsChange(updated);
                }}
                options={[
                  { value: 'sum', label: 'Sum' },
                  { value: 'avg', label: 'Average' },
                  { value: 'count', label: 'Count' },
                  { value: 'min', label: 'Min' },
                  { value: 'max', label: 'Max' },
                ]}
              />
              <Select
                label="Field"
                value={agg.field}
                onChange={(e) => {
                  const updated = [...aggregations];
                  updated[idx] = { ...agg, field: e.target.value };
                  onAggregationsChange(updated);
                }}
                options={numericFields.map((f) => ({ value: f.id, label: f.label }))}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onAggregationsChange(aggregations.filter((_, i) => i !== idx))}
                aria-label="Remove"
              >
                <svg
                  className="h-4 w-4 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </Button>
            </div>
          ))}
        </div>
        <Button
          variant="outline"
          size="sm"
          className="mt-2"
          disabled={numericFields.length === 0}
          onClick={() => {
            onAggregationsChange([
              ...aggregations,
              { field: numericFields[0].id, type: 'sum' as AggregationType },
            ]);
          }}
        >
          + Add Aggregation
        </Button>
      </div>
    </div>
  );
}

// ─── Sort Section ───────────────────────────────────────────────────────────

interface SortSectionProps {
  sortConfig: SortConfig[];
  fields: FieldDefinition[];
  onSortChange: (sort: SortConfig[]) => void;
}

function SortSection({ sortConfig, fields, onSortChange }: SortSectionProps) {
  return (
    <div>
      {sortConfig.length === 0 && (
        <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">Default order (no sorting).</p>
      )}
      <div className="space-y-2">
        {sortConfig.map((sort, idx) => (
          <div key={idx} className="flex items-end gap-2">
            <Select
              label="Field"
              value={sort.field}
              onChange={(e) => {
                const updated = [...sortConfig];
                updated[idx] = { ...sort, field: e.target.value };
                onSortChange(updated);
              }}
              options={fields.map((f) => ({ value: f.id, label: f.label }))}
            />
            <Select
              label="Direction"
              value={sort.direction}
              onChange={(e) => {
                const updated = [...sortConfig];
                updated[idx] = { ...sort, direction: e.target.value as SortDirection };
                onSortChange(updated);
              }}
              options={[
                { value: 'asc', label: 'A → Z / Low → High' },
                { value: 'desc', label: 'Z → A / High → Low' },
              ]}
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onSortChange(sortConfig.filter((_, i) => i !== idx))}
              aria-label="Remove"
            >
              <svg
                className="h-4 w-4 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </Button>
          </div>
        ))}
      </div>
      <Button
        variant="outline"
        size="sm"
        className="mt-2"
        onClick={() => {
          const first = fields.find((f) => !sortConfig.some((s) => s.field === f.id));
          if (first) onSortChange([...sortConfig, { field: first.id, direction: 'asc' }]);
        }}
      >
        + Add Sort
      </Button>
    </div>
  );
}

// ─── Calculated Fields ──────────────────────────────────────────────────────

interface CalculatedFieldsSectionProps {
  calculatedFields: CalculatedField[];
  numericFields: FieldDefinition[];
  onAdd: () => void;
  onUpdate: (id: string, updates: Partial<CalculatedField>) => void;
  onRemove: (id: string) => void;
}

function CalculatedFieldsSection({
  calculatedFields,
  numericFields,
  onAdd,
  onUpdate,
  onRemove,
}: CalculatedFieldsSectionProps) {
  return (
    <div className="space-y-3">
      {calculatedFields.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Derive metrics like realization rate, profitability, or utilization.
        </p>
      )}
      {calculatedFields.map((calc) => (
        <div
          key={calc.id}
          className="flex items-end gap-2 rounded-lg border border-border/60 p-3 dark:border-border-dark/60"
        >
          <Input
            label="Name"
            value={calc.label}
            onChange={(e) =>
              onUpdate(calc.id, {
                label: e.target.value,
                name: e.target.value.toLowerCase().replace(/\s+/g, '_'),
              })
            }
            placeholder="e.g., Profit Margin"
          />
          <Select
            label="Field A"
            value={calc.fieldA}
            onChange={(e) => onUpdate(calc.id, { fieldA: e.target.value })}
            options={numericFields.map((f) => ({ value: f.id, label: f.label }))}
          />
          <Select
            label="Op"
            value={calc.operation}
            onChange={(e) =>
              onUpdate(calc.id, { operation: e.target.value as CalculatedFieldOperation })
            }
            options={[
              { value: 'add', label: '+' },
              { value: 'subtract', label: '−' },
              { value: 'multiply', label: '×' },
              { value: 'divide', label: '÷' },
              { value: 'percentage', label: '%' },
            ]}
          />
          <Select
            label="Field B"
            value={calc.fieldB}
            onChange={(e) => onUpdate(calc.id, { fieldB: e.target.value })}
            options={numericFields.map((f) => ({ value: f.id, label: f.label }))}
          />
          <Button variant="ghost" size="sm" onClick={() => onRemove(calc.id)} aria-label="Remove">
            <svg
              className="h-4 w-4 text-gray-400 hover:text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={onAdd}>
        + Add Calculated Field
      </Button>
    </div>
  );
}

// ─── Join Sources Section ───────────────────────────────────────────────────

interface JoinSourcesSectionProps {
  source: DataSource;
  joins: ReportBuilderJoin[];
  metadata:
    | { joinPaths: Record<string, JoinPath[]>; sourceColumns: Record<string, string[]> }
    | undefined;
  maxJoins: number;
  onJoinsChange: (joins: ReportBuilderJoin[]) => void;
}

function JoinSourcesSection({
  source,
  joins,
  metadata,
  maxJoins,
  onJoinsChange,
}: JoinSourcesSectionProps) {
  const availableJoins = metadata?.joinPaths?.[source] ?? [];
  const [showManual, setShowManual] = useState(false);
  const atJoinLimit = joins.length >= maxJoins;

  const handleAddAutoJoin = useCallback(
    (jp: JoinPath) => {
      if (joins.length >= maxJoins) return;
      // Don't add duplicate
      if (joins.some((j) => j.source === jp.target)) return;
      onJoinsChange([...joins, { source: jp.target, type: 'left', label: jp.label }]);
    },
    [joins, maxJoins, onJoinsChange],
  );

  const handleAddManualJoin = useCallback(
    (target: DataSource, fromCol: string, toCol: string) => {
      if (joins.length >= maxJoins) return;
      if (joins.some((j) => j.source === target)) return;
      const label = DATA_SOURCE_OPTIONS.find((d) => d.value === target)?.label ?? target;
      onJoinsChange([
        ...joins,
        { source: target, type: 'left', from_column: fromCol, to_column: toCol, label },
      ]);
      setShowManual(false);
    },
    [joins, maxJoins, onJoinsChange],
  );

  const handleRemoveJoin = useCallback(
    (index: number) => {
      onJoinsChange(joins.filter((_, i) => i !== index));
    },
    [joins, onJoinsChange],
  );

  const handleToggleType = useCallback(
    (index: number) => {
      onJoinsChange(
        joins.map((j, i) =>
          i === index ? { ...j, type: j.type === 'left' ? 'inner' : 'left' } : j,
        ),
      );
    },
    [joins, onJoinsChange],
  );

  return (
    <div className="space-y-3">
      {/* Active joins */}
      {joins.length > 0 && (
        <div className="space-y-2">
          {joins.map((join, idx) => (
            <div
              key={idx}
              className="flex items-center gap-2 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 dark:border-brand-800 dark:bg-brand-900/20"
            >
              <span className="flex-1 text-sm font-medium text-gray-900 dark:text-gray-100">
                {join.label || join.source}
              </span>
              <button
                type="button"
                onClick={() => handleToggleType(idx)}
                className="rounded bg-gray-100 px-2 py-0.5 text-[10px] font-bold uppercase text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400"
              >
                {join.type}
              </button>
              {join.from_column && (
                <span className="text-[10px] text-gray-400">
                  {join.from_column} → {join.to_column}
                </span>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleRemoveJoin(idx)}
                aria-label="Remove join"
              >
                <svg
                  className="h-4 w-4 text-gray-400 hover:text-red-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Available auto-joins */}
      {availableJoins.length > 0 && !atJoinLimit && (
        <div>
          <p className="mb-2 text-xs font-medium text-gray-500 dark:text-gray-400">
            Quick join (auto-detected relationships):
          </p>
          <div className="flex flex-wrap gap-2">
            {availableJoins
              .filter((jp) => !joins.some((j) => j.source === jp.target))
              .map((jp) => (
                <button
                  key={jp.target}
                  type="button"
                  onClick={() => handleAddAutoJoin(jp)}
                  className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm text-gray-700 transition-colors hover:border-brand-400 hover:bg-brand-50 dark:border-border-dark dark:text-gray-300 dark:hover:border-brand-600 dark:hover:bg-brand-900/20"
                >
                  <svg
                    className="h-3.5 w-3.5 text-brand-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  {jp.label}
                </button>
              ))}
          </div>
        </div>
      )}

      {atJoinLimit && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Maximum {maxJoins} joins allowed.
        </p>
      )}

      {/* Manual join option */}
      <div className="pt-1">
        {!showManual && !atJoinLimit ? (
          <button
            type="button"
            onClick={() => setShowManual(true)}
            className="text-xs font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400"
          >
            + Custom join (specify columns manually)
          </button>
        ) : showManual && !atJoinLimit ? (
          <ManualJoinForm
            source={source}
            metadata={metadata}
            existingJoins={joins}
            onAdd={handleAddManualJoin}
            onCancel={() => setShowManual(false)}
          />
        ) : null}
      </div>

      {joins.length === 0 && !showManual && availableJoins.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No automatic join paths available from {source}. Use custom join to connect manually.
        </p>
      )}
    </div>
  );
}

// ─── Manual Join Form ───────────────────────────────────────────────────────

interface ManualJoinFormProps {
  source: DataSource;
  metadata: { sourceColumns: Record<string, string[]> } | undefined;
  existingJoins: ReportBuilderJoin[];
  onAdd: (target: DataSource, fromCol: string, toCol: string) => void;
  onCancel: () => void;
}

function ManualJoinForm({ source, metadata, existingJoins, onAdd, onCancel }: ManualJoinFormProps) {
  const [target, setTarget] = useState<DataSource | ''>('');
  const [fromCol, setFromCol] = useState('');
  const [toCol, setToCol] = useState('');

  // Only allow joining to report groups (sources) the user may access.
  const { can, canAny, isAdmin } = usePermissions();
  const sourceColumns = metadata?.sourceColumns?.[source] ?? [];
  const targetColumns = target ? (metadata?.sourceColumns?.[target] ?? []) : [];
  const availableTargets = DATA_SOURCE_OPTIONS.filter(
    (d) =>
      d.value !== source &&
      !existingJoins.some((j) => j.source === d.value) &&
      canAccessReportSource({ isAdmin, can, canAny }, d.value),
  );

  return (
    <div className="rounded-lg border border-border p-3 dark:border-border-dark">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Select
          label="Target Source"
          value={target}
          onChange={(e) => {
            setTarget(e.target.value as DataSource);
            setToCol('');
          }}
          options={availableTargets.map((d) => ({ value: d.value, label: d.label }))}
          placeholder="Select..."
        />
        <Select
          label={`From (${source})`}
          value={fromCol}
          onChange={(e) => setFromCol(e.target.value)}
          options={sourceColumns.map((c) => ({ value: `${source}.${c}`, label: c }))}
          placeholder="Column..."
        />
        <Select
          label={`To (${target || '...'})`}
          value={toCol}
          onChange={(e) => setToCol(e.target.value)}
          options={targetColumns.map((c) => ({ value: `${target}.${c}`, label: c }))}
          placeholder="Column..."
          disabled={!target}
        />
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Button
          size="sm"
          onClick={() => target && fromCol && toCol && onAdd(target as DataSource, fromCol, toCol)}
          disabled={!target || !fromCol || !toCol}
        >
          Add Join
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ─── Data Rule Presets ──────────────────────────────────────────────────────

interface DataRulePresetsProps {
  rules: ReportingDataRule[];
  selectedIds: string[];
  source: DataSource;
  onSelectionChange: (ids: string[]) => void;
}

function DataRulePresets({ rules, selectedIds, source, onSelectionChange }: DataRulePresetsProps) {
  // Filter to rules that apply to the current data source
  const applicableRules = rules.filter(
    (r) => r.entities.length === 0 || r.entities.includes(source),
  );

  const handleToggle = useCallback(
    (ruleId: string) => {
      onSelectionChange(
        selectedIds.includes(ruleId)
          ? selectedIds.filter((id) => id !== ruleId)
          : [...selectedIds, ruleId],
      );
    },
    [selectedIds, onSelectionChange],
  );

  if (applicableRules.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        No data rules configured for reporting on this data source. Create them under Settings →
        Data Authorization.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Select data rules to apply as additional filters. These restrict the report to matching
        records only.
      </p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {applicableRules.map((rule) => (
          <label
            key={rule.id}
            className={`flex cursor-pointer items-start gap-3 rounded-lg border-2 p-3 transition-all ${
              selectedIds.includes(rule.id)
                ? 'border-brand-500 bg-brand-50 dark:border-brand-400 dark:bg-brand-900/20'
                : 'border-border hover:border-gray-300 dark:border-border-dark dark:hover:border-gray-600'
            }`}
          >
            <input
              type="checkbox"
              checked={selectedIds.includes(rule.id)}
              onChange={() => handleToggle(rule.id)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
            />
            <div className="flex-1 min-w-0">
              <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                {rule.name}
              </span>
              {rule.description && (
                <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                  {rule.description}
                </span>
              )}
              <span className="mt-1 block text-xs text-gray-400 dark:text-gray-500">
                {rule.conditions.map((c, i) => (
                  <span key={i}>
                    {i > 0 && <span className="font-semibold"> {rule.logic.toUpperCase()} </span>}
                    {c.field} {c.operator} {c.values.slice(0, 2).join(', ')}
                    {c.values.length > 2 ? ` +${c.values.length - 2}` : ''}
                  </span>
                ))}
              </span>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}

// ─── Save Report Dialog ─────────────────────────────────────────────────────

interface SaveReportDialogProps {
  onSave: (name: string, description?: string) => void;
  onCancel: () => void;
  isSaving: boolean;
}

function SaveReportDialog({ onSave, onCancel, isSaving }: SaveReportDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="save-builder-report-title"
    >
      <div className="w-full max-w-md rounded-xl border border-border bg-white p-6 shadow-xl dark:border-border-dark dark:bg-surface-dark-secondary">
        <h3
          id="save-builder-report-title"
          className="text-lg font-semibold text-gray-900 dark:text-gray-100"
        >
          Save Report
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Save this configuration to re-run or share later
        </p>

        <div className="mt-4 space-y-4">
          <Input
            label="Report Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Monthly Revenue by Client"
            required
          />
          <Input
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Brief description..."
          />
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <Button variant="outline" onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={() => onSave(name, description || undefined)}
            disabled={isSaving || !name.trim()}
          >
            {isSaving ? 'Saving...' : 'Save Report'}
          </Button>
        </div>
      </div>
    </div>
  );
}
